import { type ComponentProps, useCallback, useLayoutEffect, useRef } from 'react';

import { cn } from '../cn';
import type { ColorName, ColorVariant, Style } from './buttonClasses';
import { composeVariant, inputVariantClasses, inputVariantPlaceholder } from './buttonClasses';
import type { BaseProps, Size } from './types';
import {
  shapeRounded,
  sizeFont,
  sizeIconClass,
  sizeIconPaddingL,
  sizeIconPaddingR,
  sizeInnerGap,
  sizeMinHeight,
  sizePaddingL,
  sizePaddingR,
} from './types';

export type TextareaProps = Omit<ComponentProps<'textarea'>, 'size'> &
  BaseProps & {
    size?: Size;
    /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
    color?: ColorName;
    /** Style axis — `'filled' | 'outline' | 'soft'`. Defaults to `'filled'`. */
    tone?: Style;
    collapsed?: boolean;
  };

export default function Textarea({
  size = 'md',
  bordered = true,
  shape,
  icon,
  rIcon,
  error,
  color,
  tone,
  className,
  disabled,
  value,
  defaultValue,
  rows = 1,
  collapsed,
  onChange,
  ref,
  readOnly,
  isUpdating: _isUpdating,
  isSuccess: _isSuccess,
  ...props
}: TextareaProps & { isUpdating?: boolean; isSuccess?: boolean }) {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = useCallback(() => {
    const el = internalRef.current;
    if (!el) return;
    if (collapsed) {
      el.style.height = '';
      return;
    }
    el.style.height = '0px';
    const scrollH = el.scrollHeight;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const minH = lineHeight * rows;
    el.style.height = `${Math.max(scrollH, minH)}px`;
  }, [collapsed, rows]);

  useLayoutEffect(() => {
    adjustHeight();
  }, [adjustHeight, value]);

  // Re-measure when the textarea becomes visible (e.g. tab switch)
  useLayoutEffect(() => {
    const el = internalRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => adjustHeight());
    ro.observe(el);
    return () => ro.disconnect();
  }, [adjustHeight]);

  const verticalPad = {
    '3xs': 'py-0.5',
    '2xs': 'py-0.5',
    xs: 'py-1',
    sm: 'py-1.5',
    md: 'py-2',
    lg: 'py-2',
    xl: 'py-3',
    '2xl': 'py-4',
    '3xl': 'py-5',
    '4xl': 'py-6',
  }[size];

  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');

  return (
    <div
      className={cn(
        'flex items-center font-sans transition-colors',
        collapsed && 'overflow-hidden',
        sizeMinHeight(size),
        shapeRounded(shape, size),
        sizeFont(size),
        sizeInnerGap(size),
        !icon && sizePaddingL(size),
        !rIcon && sizePaddingR(size),
        verticalPad,
        'border',
        inputVariantClasses(effectiveVariant, bordered, !!error, !!disabled, !!readOnly),
        className,
      )}>
      {icon && (
        <span className={cn('shrink-0 flex items-center justify-center', sizeIconPaddingL(size))}>
          <span className={cn(sizeIconClass(size), '*:size-full')}>{icon}</span>
        </span>
      )}
      <textarea
        ref={(el) => {
          internalRef.current = el;
          if (typeof ref === 'function') ref(el);
          else if (ref) ref.current = el;
        }}
        disabled={disabled}
        readOnly={readOnly}
        value={value}
        defaultValue={defaultValue}
        rows={rows}
        className={cn(
          'flex-1 min-w-0 bg-transparent outline-hidden border-none resize-none',
          collapsed && 'whitespace-nowrap overflow-hidden',
          inputVariantPlaceholder(effectiveVariant, !!error),
          disabled && 'cursor-not-allowed',
          readOnly && 'cursor-default',
        )}
        {...props}
        onChange={(e) => {
          adjustHeight();
          onChange?.(e);
        }}
      />
      {rIcon && (
        <span className={cn('shrink-0 flex items-center justify-center', sizeIconPaddingR(size))}>
          <span className={cn(sizeIconClass(size), '*:size-full')}>{rIcon}</span>
        </span>
      )}
    </div>
  );
}
